import {
  ActionIcon,
  Badge,
  Group,
  Loader,
  Menu,
  Text,
  Tooltip
} from "@mantine/core";
import {
  IconAlertTriangleFilled,
  IconChecklist,
  IconDotsVertical
} from "@tabler/icons-react";
import classNames from "classnames";
import { MantineRoundedNumberCell } from "../../../mantine/mrt/components/cell/MantineRoundedNumberCell";
import { roundWithPrecision } from "../../../utils/roundWithPrecision";
import { PRECISION_FOR_OUTDATED } from "../EnergyRevenuesPage/EnergyRevenues.constants";
import { VERIFICATION_OPTIONS } from "../EnergyRevenuesPage/EnergyRevenuesVerificationTable/columns/getStatusColumn";
import { useEnergyRevenuesPageParams } from "../hooks/useEnergyRevenuesPageParams";
import type { EnergyRevenuesCellCheckStateResponse } from "../RevenueMonitoring.types";
import "./DataVerificationCell.scss";

interface DataVerificationCellProps {
  value?: number;
  warningMessage?: string;
  isCurrentMonth?: boolean;
  verificationItem?: EnergyRevenuesCellCheckStateResponse;
  isPending?: boolean;
  onVerificationClick: (
    verificationItemToUpdate?: EnergyRevenuesCellCheckStateResponse
  ) => void;
}

function DataVerificationCell({
  value = 0,
  warningMessage,
  isCurrentMonth,
  verificationItem,
  isPending,
  onVerificationClick
}: DataVerificationCellProps) {
  const hasVerification = !!verificationItem;
  const isVerificationOutdated =
    hasVerification &&
    roundWithPrecision(
      verificationItem.calculated_amount,
      PRECISION_FOR_OUTDATED
    ) !== roundWithPrecision(value, PRECISION_FOR_OUTDATED);

  const verificationDetails = VERIFICATION_OPTIONS.find(
    (option) => option.value === verificationItem?.state
  );

  const { setVerificationDetailView } = useEnergyRevenuesPageParams();

  function getTooltipContent() {
    return (
      <>
        <Text>
          Letze Änderung am:{" "}
          {verificationItem?.timestamp_of_state_change || "unbekannt"}
          <br />
          Geändert durch:{" "}
          {verificationItem?.author_of_state_change_name || "unbekannt"}
          <br />
          <Badge color={verificationDetails?.color} component="span">
            {verificationDetails?.label}
          </Badge>
        </Text>
        {isVerificationOutdated && (
          <Group gap="xs" mt="xs" wrap="nowrap">
            <IconAlertTriangleFilled
              color="var(--mantine-color-warning-filled)"
              size={16}
            />
            <Text>Wert hat sich seit der letzten Prüfung geändert</Text>
          </Group>
        )}
      </>
    );
  }

  return (
    <Tooltip disabled={!hasVerification} label={getTooltipContent()}>
      <Group
        className={classNames("DataVerificationCell", {
          "has-verification": hasVerification,
          "menu-hidden": hasVerification && !isVerificationOutdated,
          "menu-disabled": isPending
        })}
        gap={0}
        wrap="nowrap"
      >
        <MantineRoundedNumberCell
          decimalScale={PRECISION_FOR_OUTDATED}
          value={value}
          warningMessage={warningMessage}
        />
        {!hasVerification || isVerificationOutdated ? (
          <Menu disabled={isPending} shadow="md" width={200}>
            <Menu.Target>
              <ActionIcon disabled={isPending} variant="transparent">
                {hasVerification && !isPending && (
                  <IconChecklist
                    className="icon-checked-status"
                    color="var(--mantine-color-warning-filled"
                    role="img"
                    size={16}
                    title="Prüfung hinterlegt, Referenzwert veraltet"
                  />
                )}
                {isPending ? (
                  <Loader color="gray" size={16} />
                ) : (
                  <IconDotsVertical
                    className="icon-menu"
                    size={16}
                    title="Aktionen für veralteten Referenzwert"
                  />
                )}
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown p={0}>
              <Tooltip
                disabled={!isCurrentMonth}
                label="Für den laufenden Monat ist keine Prüfung möglich."
              >
                <Menu.Item
                  disabled={isCurrentMonth}
                  leftSection={<IconChecklist size={16} />}
                  onClick={() => onVerificationClick(verificationItem)}
                >
                  Prüfung {isVerificationOutdated ? "erneuern" : "hinterlegen"}
                </Menu.Item>
              </Tooltip>
            </Menu.Dropdown>
          </Menu>
        ) : (
          <ActionIcon
            disabled={isPending}
            variant="transparent"
            onClick={() => {
              setVerificationDetailView(verificationItem?.id);
            }}
          >
            <IconChecklist
              className="icon-checked-status"
              color={`var(--mantine-color-${verificationDetails?.color || "gray"}-9)`}
              role="img"
              size={16}
              title="Prüfung für Wert hinterlegt"
            />
          </ActionIcon>
        )}
      </Group>
    </Tooltip>
  );
}

export { DataVerificationCell, DataVerificationCellProps };
